<template>
  <v-card outlined class="mt-5">
    <v-card-title class="d-flex align-center">
      <h2>Facility Daily Checks</h2>
      <v-spacer></v-spacer>
      <v-btn
        v-if="can('facilities.manage_daily_checklist')"
        small
        outlined
        color="primary"
        @click="manageDialog = true"
      >
        <v-icon left small>mdi-format-list-checks</v-icon> Manage Checklist
      </v-btn>
    </v-card-title>

    <manage-daily-checklist-modal
      :show.sync="manageDialog"
      @changed="loadToday"
    />

    <v-tabs v-model="activeTab">
      <v-tab>Submit Check</v-tab>
      <v-tab>History</v-tab>
    </v-tabs>
    <v-tabs-items v-model="activeTab">
      <!-- Submit Check -->
      <v-tab-item>
        <v-card-text v-if="loadingToday" class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-card-text>

        <!-- Already submitted today (read-only) -->
        <v-card-text v-else-if="todayCheck && !editing">
          <v-alert type="success" outlined>
            <div class="d-flex align-center">
              <span>Today's daily check has already been submitted.</span>
              <v-spacer></v-spacer>
              <v-btn
                v-if="can('facilities.edit_daily_check')"
                small
                color="primary"
                outlined
                @click="startEdit"
              >
                <v-icon left small>mdi-pencil</v-icon> Edit
              </v-btn>
            </div>
          </v-alert>
          <p class="caption grey--text mb-0">Date</p>
          <p>{{ todayCheck.check_date }}</p>
          <v-row dense>
            <v-col
              v-for="item in housekeepingItems"
              :key="item.id"
              cols="12"
              sm="6"
            >
              <v-icon :color="item.checked ? 'green' : 'red'" small>
                {{ item.checked ? "mdi-check-circle" : "mdi-close-circle" }}
              </v-icon>
              {{ item.label }}
            </v-col>
          </v-row>
          <template v-if="securityItems.length">
            <v-divider class="my-3"></v-divider>
            <p class="subtitle-2 mb-2">Security &amp; Systems</p>
            <v-row dense>
              <v-col
                v-for="item in securityItems"
                :key="item.id"
                cols="12"
                sm="4"
              >
                <v-icon :color="item.checked ? 'green' : 'red'" small>
                  {{ item.checked ? "mdi-check-circle" : "mdi-close-circle" }}
                </v-icon>
                {{ item.label }}
              </v-col>
            </v-row>
          </template>
          <template v-if="todayCheck.notes">
            <p class="caption grey--text mb-0 mt-4">Notes</p>
            <p>{{ todayCheck.notes }}</p>
          </template>
        </v-card-text>

        <!-- Edit already-submitted check -->
        <v-card-text v-else-if="todayCheck && editing">
          <v-alert type="info" outlined dense>
            Editing today's submitted daily check.
          </v-alert>
          <p class="subtitle-1">Daily Facility Checklist</p>
          <v-checkbox
            v-for="item in editHousekeeping"
            :key="'e' + item.facility_daily_check_option_id"
            v-model="item.checked"
            :label="item.label"
            color="green"
            hide-details
            class="mt-1"
          ></v-checkbox>
          <template v-if="editSecurity.length">
            <v-divider class="my-4"></v-divider>
            <p class="subtitle-1">Security &amp; Systems</p>
            <v-checkbox
              v-for="item in editSecurity"
              :key="'es' + item.facility_daily_check_option_id"
              v-model="item.checked"
              :label="item.label"
              color="green"
              hide-details
              class="mt-1"
            ></v-checkbox>
          </template>
          <v-textarea
            v-model="editForm.notes"
            label="Notes"
            rows="2"
            auto-grow
            outlined
            class="mt-4"
          ></v-textarea>
          <v-btn color="primary" :loading="saving" @click="saveEdit">
            <v-icon left>mdi-content-save</v-icon> Save Changes
          </v-btn>
          <v-btn text class="ml-2" :disabled="saving" @click="cancelEdit">
            Cancel
          </v-btn>
        </v-card-text>

        <!-- Checklist form -->
        <v-card-text v-else>
          <p class="subtitle-1">Daily Facility Checklist</p>
          <v-alert v-if="!activeOptions.length" type="info" outlined>
            No checklist items configured.
            <template v-if="can('facilities.manage_daily_checklist')">
              Use <strong>Manage Checklist</strong> to add items.
            </template>
          </v-alert>
          <v-checkbox
            v-for="opt in housekeepingOptions"
            :key="opt.id"
            v-model="checks[opt.id]"
            :label="opt.label"
            color="green"
            hide-details
            class="mt-1"
          ></v-checkbox>
          <template v-if="securityOptions.length">
            <v-divider class="my-4"></v-divider>
            <p class="subtitle-1">Security &amp; Systems</p>
            <v-checkbox
              v-for="opt in securityOptions"
              :key="opt.id"
              v-model="checks[opt.id]"
              :label="opt.label"
              color="green"
              hide-details
              class="mt-1"
            ></v-checkbox>
          </template>
          <v-textarea
            v-model="form.notes"
            label="Notes"
            rows="2"
            auto-grow
            outlined
            class="mt-4"
          ></v-textarea>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!activeOptions.length"
            @click="submit"
          >
            <v-icon left>mdi-send</v-icon> Submit Check
          </v-btn>
        </v-card-text>
      </v-tab-item>

      <!-- History -->
      <v-tab-item>
        <v-card class="my-4" flat outlined>
          <v-card-text class="pb-2">
            <v-row align="center" dense>
              <v-col cols="12" sm="6" md="2">
                <v-menu
                  v-model="fromMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filters.from_date"
                      label="From Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      clearable
                    />
                  </template>
                  <v-date-picker
                    v-model="filters.from_date"
                    no-title
                    @input="fromMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-menu
                  v-model="toMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filters.to_date"
                      label="To Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      clearable
                    />
                  </template>
                  <v-date-picker
                    v-model="filters.to_date"
                    no-title
                    @input="toMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="2"
                v-if="can('reports.branch_filter')"
              >
                <v-select
                  v-model="filters.branch_id"
                  :items="branches"
                  item-text="branch_name"
                  item-value="id"
                  label="Branch"
                  clearable
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="primary"
                  @click="search"
                  :loading="loadingHistory"
                >
                  <v-icon left>mdi-magnify</v-icon>Search
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-data-table :items="daily_checks" :headers="historyHeaders">
          <template v-slot:item.branch="{ item }">
            {{ item.branch ? item.branch.branch_name : "—" }}
          </template>
          <template v-slot:item.checked_by="{ item }">
            {{
              item.checked_by
                ? item.checked_by.full_name || item.checked_by.username
                : "—"
            }}
          </template>
          <template v-slot:item.result="{ item }">
            <v-chip
              small
              :color="
                (item.passed_count || 0) === (item.items_count || 0)
                  ? 'green'
                  : 'orange'
              "
              dark
            >
              {{ item.passed_count || 0 }}/{{ item.items_count || 0 }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="primary" @click="viewCheck(item)">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </template>
        </v-data-table>
        <daily-check-detail-modal
          :show.sync="detailDialog"
          :check-id="selectedCheckId"
        />
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import ManageDailyChecklistModal from "./manageDailyChecklistModal.vue";
import DailyCheckDetailModal from "./dailyCheckDetailModal.vue";
import { mapActions, mapGetters } from "vuex";
export default {
  components: { ManageDailyChecklistModal, DailyCheckDetailModal },
  computed: {
    ...mapGetters({
      daily_check_today: "facilities/daily_check_today",
      daily_checks: "facilities/daily_checks",
      branches: "branch/branches",
      hasPermission: "permission/hasPermission",
    }),
    todayCheck() {
      return this.daily_check_today ? this.daily_check_today.check : null;
    },
    activeOptions() {
      return (this.daily_check_today && this.daily_check_today.options) || [];
    },
    housekeepingOptions() {
      return this.activeOptions.filter((o) => o.section !== "security");
    },
    securityOptions() {
      return this.activeOptions.filter((o) => o.section === "security");
    },
    housekeepingItems() {
      return this.todayCheck
        ? (this.todayCheck.items || []).filter((i) => i.section !== "security")
        : [];
    },
    securityItems() {
      return this.todayCheck
        ? (this.todayCheck.items || []).filter((i) => i.section === "security")
        : [];
    },
    editHousekeeping() {
      return this.editItems.filter((i) => i.section !== "security");
    },
    editSecurity() {
      return this.editItems.filter((i) => i.section === "security");
    },
    historyHeaders() {
      return [
        { text: "Date", value: "check_date" },
        { text: "Branch", value: "branch", sortable: false },
        { text: "Checked By", value: "checked_by", sortable: false },
        { text: "Result", value: "result", sortable: false },
        { text: "Notes", value: "notes" },
        { text: "Actions", value: "actions", sortable: false },
      ];
    },
  },
  data() {
    return {
      activeTab: 0,
      loadingToday: false,
      submitting: false,
      saving: false,
      editing: false,
      loadingHistory: false,
      fromMenu: false,
      toMenu: false,
      manageDialog: false,
      detailDialog: false,
      selectedCheckId: null,
      checks: {},
      editItems: [],
      form: {
        notes: "",
      },
      editForm: {
        notes: "",
      },
      filters: {
        from_date: this.defaultFromDate(),
        to_date: this.defaultToDate(),
        branch_id: null,
      },
    };
  },
  methods: {
    ...mapActions({
      get_daily_check_today: "facilities/get_daily_check_today",
      submit_daily_check: "facilities/submit_daily_check",
      update_daily_check: "facilities/update_daily_check",
      get_daily_checks: "facilities/get_daily_checks",
    }),
    can(key) {
      return this.hasPermission(key);
    },
    startEdit() {
      const check = this.todayCheck;
      this.editForm = { notes: check.notes || "" };
      this.editItems = (check.items || []).map((i) => ({
        facility_daily_check_option_id: i.facility_daily_check_option_id,
        label: i.label,
        section: i.section,
        checked: !!i.checked,
      }));
      this.editing = true;
    },
    cancelEdit() {
      this.editing = false;
    },
    async saveEdit() {
      this.saving = true;
      try {
        await this.update_daily_check({
          id: this.todayCheck.id,
          notes: this.editForm.notes,
          items: this.editItems.map((i) => ({
            facility_daily_check_option_id: i.facility_daily_check_option_id,
            checked: i.checked,
          })),
        });
        await this.$alert({
          title: "Saved",
          message: "Today's daily check has been updated.",
          color: "success",
        });
        this.editing = false;
        await this.loadToday();
      } catch (e) {
        console.error(e);
        this.$alert({
          title: "Error",
          message:
            e.response &&
            (e.response.status === 422 || e.response.status === 403)
              ? e.response.data.message
              : "Failed to update the daily check.",
          color: "error",
        });
      }
      this.saving = false;
    },
    defaultFromDate() {
      let d = new Date();
      d.setDate(d.getDate() - 30);
      return d.toISOString().substring(0, 10);
    },
    defaultToDate() {
      return new Date().toISOString().substring(0, 10);
    },
    async loadToday() {
      this.loadingToday = true;
      try {
        await this.get_daily_check_today();
        // Start every active checklist item unchecked; the user ticks each one.
        const map = {};
        this.activeOptions.forEach((o) => {
          map[o.id] = false;
        });
        this.checks = map;
      } catch (e) {
        console.error(e);
      }
      this.loadingToday = false;
    },
    async submit() {
      this.submitting = true;
      try {
        await this.submit_daily_check({
          notes: this.form.notes,
          items: this.activeOptions.map((o) => ({
            facility_daily_check_option_id: o.id,
            checked: !!this.checks[o.id],
          })),
        });
        await this.$alert({
          title: "Submitted",
          message: "Today's daily check has been submitted.",
          color: "success",
        });
        await this.loadToday();
      } catch (e) {
        console.error(e);
        this.$alert({
          title: "Error",
          message:
            e.response && e.response.status === 422
              ? e.response.data.message
              : "Failed to submit the daily check.",
          color: "error",
        });
      }
      this.submitting = false;
    },
    async search() {
      this.loadingHistory = true;
      try {
        await this.get_daily_checks(this.filters);
      } catch (e) {
        console.error(e);
      }
      this.loadingHistory = false;
    },
    viewCheck(item) {
      this.selectedCheckId = item.id;
      this.detailDialog = true;
    },
  },
  watch: {
    activeTab(val) {
      if (val === 1) this.search();
    },
    filters: {
      deep: true,
      handler() {
        if (this.activeTab === 1) this.search();
      },
    },
  },
  mounted() {
    this.loadToday();
    if (!this.branches || this.branches.length === 0) {
      this.$store.dispatch("branch/get_branches");
    }
  },
};
</script>
